import type { OnMount } from "@monaco-editor/react";
import { FormatBold, FormatItalic } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  styled,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  ToggleButtonProps,
  Typography,
} from "@mui/material";
import {
  FieldInputProps,
  FieldMetaProps,
  FastField,
  Field,
  FormikBag,
} from "formik";
import { debounce } from "lodash";
import type { editor } from "monaco-editor";
import { useSnackbar } from "notistack";
import {
  ChangeEvent,
  lazy,
  ReactElement,
  ReactNode,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";

import LiquidTemplate, {
  ALLOWED_FILTERS,
  ALLOWED_TAGS,
  LiquidTemplateParseResult,
} from "@stw/liquid-template";
import { isBold, isItalic, toggleBold, toggleItalic } from "@stw/unicode-fonts";

import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import theme, { palette } from "~frontend/theme";
import { contactFieldNameSchema } from "~frontend/util/contactFields";

import { FlexBox } from "../FlexBox";
import { ExternalLink } from "../Link";
import TooltipButton from "../TooltipButton";
import { configureLiquidIntellisense } from "./monaco/liquid-intellisense";

const MonacoEditor = lazy(() => import("../MonacoEditor"));

type Style = "bold" | "italic";
const STYLERS: { [key in Style]: (original: string) => string } = {
  bold: toggleBold,
  italic: toggleItalic,
};

type FormScriptFieldProps = {
  label?: string;
  secondaryLabel?: string | ReactElement;
  value: string;
  contactFields?: { name: string; isMapped: boolean }[];
  onCreateContactField?: (name: string) => Promise<void>;
  error?: string;
  onChange?: (value: string) => void;
  onParse?: (parseResult: LiquidTemplateParseResult) => void;
  onBlur?: () => void;
  hideContactFields?: boolean;
  media?: ReactNode;
  additionalButtons?: ToggleButtonProps[];
};

const EditorFormControl = styled(FormControl)({
  marginBottom: "10px",
});

const EditorWrapper = styled("div")({
  border: "1px solid",
  borderRadius: 0,
  borderColor: palette.lightGray,
  transition: "border-color 0.2s ease-in-out",
  backgroundColor: "white",
});

const StyleButton = styled(ToggleButton)({
  [`&.${toggleButtonGroupClasses.selected}`]: {
    backgroundColor: palette.darkGray,
    color: palette.white,
  },
});

const ContactFieldButton = styled(Button)(() => ({
  padding: "2px 6px 2px 6px",
  minWidth: 0,
  fontSize: "12px",
  fontWeight: "400",
  marginRight: "5px",
  marginBottom: "5px",
  borderRadius: "1rem",
  background: palette.midBlue,
  textTransform: "none",
  color: palette.navy,
  "&:hover": {
    background: palette.navy,
    borderColor: palette.navy,
    color: "white",
  },

  "&:disabled": {
    background: palette.lightGray,
    borderColor: palette.lightGray,
  },
}));

const AddNewContactFieldButtonStyle = {
  textDecoration: "none",
  color: palette.navy,
  padding: "2px 6px 2px 6px",
  marginBottom: "5px",
  fontWeight: "500",
};

function AddContactFieldForm({
  onCancel,
  onSubmit,
  contactFieldNames,
}: {
  onCancel: () => any;
  onSubmit: (name: string) => any;
  contactFieldNames: string[];
}): ReactElement {
  const [name, setName] = useState("");

  let validationError: string | null = null;
  try {
    contactFieldNameSchema({ otherFieldNames: contactFieldNames }).validateSync(
      name
    );
  } catch (error) {
    validationError = (error as Error).message;
  }

  const handleChangeName = (event: ChangeEvent<HTMLInputElement>) =>
    setName(event.target.value);

  const handleSubmit = () => onSubmit(name);

  return (
    <div>
      <TextField
        value={name}
        onChange={handleChangeName}
        variant="standard"
        error={name ? !!validationError : false}
        helperText={name ? validationError : null}
      />
      <Button disabled={!!validationError} onClick={handleSubmit}>
        Save
      </Button>
      <Button onClick={onCancel}>Cancel</Button>
    </div>
  );
}

const ScriptFieldLabelStyle = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  fontSize: 12,
  color: palette.darkGray,
  paddingBottom: theme.spacing(0.5),
};

export function ScriptFieldLabel({
  label,
  secondaryLabel,
}: {
  label?: string;
  secondaryLabel?: string | ReactElement;
}): ReactElement {
  return (
    <div style={ScriptFieldLabelStyle}>
      {label ? (
        <div style={{ fontWeight: 600, minWidth: "120px" }}>{label}</div>
      ) : (
        <div />
      )}
      {secondaryLabel ? <div>{secondaryLabel}</div> : <div />}
    </div>
  );
}

export default function FormScriptField({
  value,
  onChange,
  onParse,
  onBlur,
  contactFields = [],
  onCreateContactField,
  label,
  secondaryLabel,
  error,
  hideContactFields = false,
  media,
  additionalButtons,
}: FormScriptFieldProps): ReactElement {
  const [height, setHeight] = useState("18px");
  const [focused, setFocused] = useState(false);
  const [newContactFieldFormOpen, setNewContactFieldFormOpen] = useState(false);
  const editorRef = useRef<editor.IStandaloneCodeEditor>(undefined);
  const snackbar = useSnackbar();
  const unicodeStylesEnabled = useFeatureFlag("unicodeStyleEditor");

  // We use a ref to store the contact field names so that we can update them
  // when the list of variable names changes. This is necessary because the
  // `configureLiquidIntellisense` function is called only once when the editor
  // is mounted, but we need to update the list of variables when new contact
  // fields are added.
  const variableNamesRef = useRef<string[]>([]);

  useEffect(() => {
    variableNamesRef.current = contactFields.map(field => field.name);
  }, [contactFields]);

  useEffect(() => {
    const windowResizeHandler = debounce(() => {
      editorRef.current?.layout({} as any);
    }, 250);

    window.addEventListener("resize", windowResizeHandler);
    return () => window.removeEventListener("resize", windowResizeHandler);
  }, []);

  function handleContentChange(
    newValue: string,
    { parseOnly }: { parseOnly?: boolean } = {}
  ) {
    if (!parseOnly) {
      onChange?.(newValue);
    }

    if (onParse) {
      const parseResult = LiquidTemplate.fromString(newValue, {
        validateVariables: variableNamesRef.current,
      });

      onParse(parseResult);
    }

    updateBoldAndItalicSelected();
  }

  const getSelection = (): {
    textBefore: string;
    selectedText: string;
    textAfter: string;
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
  } | null => {
    const e = editorRef.current;
    const selection = e?.getSelection();
    if (!selection) {
      return null;
    }

    const value = e!.getValue();
    const lines = value.split("\n");

    const charIndexForPosition = ({
      lineNumber,
      column,
    }: {
      lineNumber: number;
      column: number;
    }): number => {
      const linesBefore = lines.slice(0, lineNumber);
      const line = lines[lineNumber];
      return [...linesBefore, line.slice(0, column)].join("\n").length;
    };
    const startIndex = charIndexForPosition({
      lineNumber: selection.startLineNumber - 1,
      column: selection?.startColumn - 1,
    });
    const endIndex = charIndexForPosition({
      lineNumber: selection.endLineNumber - 1,
      column: selection.endColumn - 1,
    });

    const textBefore = value.slice(0, startIndex);
    const selectedText = value.slice(startIndex, endIndex);
    const textAfter = value.slice(endIndex);

    return {
      textBefore,
      selectedText,
      textAfter,
      startLineNumber: selection.startLineNumber,
      startColumn: selection.startColumn,
      endLineNumber: selection.endLineNumber,
      endColumn: selection.endColumn,
    };
  };

  const [boldSelected, setBoldSelected] = useState(false);
  const [italicSelected, setItalicSelected] = useState(false);

  const toggleStyle = useRef<(style: Style) => void>(undefined);

  const updateBoldAndItalicSelected = () => {
    const selection = getSelection();
    if (!selection) {
      return;
    }
    if (selection.selectedText) {
      setBoldSelected(isBold(selection.selectedText));
      setItalicSelected(isItalic(selection.selectedText));
    } else {
      setBoldSelected(false);
      setItalicSelected(false);
    }
  };

  const [formattingError, setFormattingError] = useState<string | null>(null);
  const [formattingErrorTimer, setFormattingErrorTimer] =
    useState<NodeJS.Timeout | null>(null);
  const showFormattingError = (error: string) => {
    if (formattingErrorTimer) {
      clearTimeout(formattingErrorTimer);
    }
    setFormattingError(error);
    setFormattingErrorTimer(setTimeout(() => setFormattingError(null), 10000));

    return () => {
      if (formattingErrorTimer) {
        clearTimeout(formattingErrorTimer);
      }
    };
  };

  const editorDidMount: OnMount = (editorMounted, monaco) => {
    editorRef.current = editorMounted;

    // NOTE: the theme must be defined and set here, as for some reason
    // setting it on the global editor object does work even though
    // the docs suggests otherwise. I may be missing something!
    const STW_THEME_NAME = "stw-script-editor";
    monaco.editor.defineTheme(STW_THEME_NAME, {
      base: "vs",
      inherit: true,
      rules: [
        {
          token: "variable.liquid",
          // NOTE: this color is overriden. See "hack" below
          foreground: "#000000",
        },
        {
          token: "delimiter.output.liquid",
          // NOTE: this color is overriden. See "hack" below
          foreground: "#000000",
        },
      ],
      colors: {
        "editor.foreground": palette.navy,
      },
    });
    monaco.editor.setTheme(STW_THEME_NAME);

    const disposible = configureLiquidIntellisense(monaco, {
      getVariables: () => variableNamesRef.current,
      filters: ALLOWED_FILTERS,
      tags: ALLOWED_TAGS,
    });
    monaco.editor.onWillDisposeModel(disposible.dispose);

    handleContentChange(editorMounted.getValue(), { parseOnly: true });

    editorMounted.onDidFocusEditorText(() => {
      setFocused(true);
    });

    editorMounted.onDidContentSizeChange(() => {
      const contentHeight = Math.min(1000, editorMounted.getContentHeight());
      setHeight(`${contentHeight}px`);
    });

    editorMounted.onDidChangeModelContent(() => {
      handleContentChange(editorMounted.getValue());
    });

    editorMounted.onDidChangeCursorSelection(() => {
      updateBoldAndItalicSelected();
    });

    editorMounted.onDidBlurEditorWidget(() => {
      setFocused(false);
      onBlur?.();
    });

    toggleStyle.current = (style: Style) => {
      const selection = getSelection();
      if (!selection?.selectedText) {
        showFormattingError("Highlight text to style");
        return;
      }
      if (
        selection.selectedText.includes("{") ||
        selection.selectedText.includes("}")
      ) {
        showFormattingError(
          "Bold and Italic are not supported for contact fields"
        );
        return;
      }

      if (selection.selectedText.match(/(https?:\/\/[^\s]+[^\s.?!])/)) {
        showFormattingError("Bold and Italic are not supported for links");
        return;
      }

      const replacedText = STYLERS[style](selection.selectedText);

      const linesBeforeNewSelectionEnd = [selection.textBefore, replacedText]
        .join("")
        .split("\n");
      const newSelectionEndLineNumber = linesBeforeNewSelectionEnd.length;
      const newSelectionEndColumn =
        linesBeforeNewSelectionEnd[linesBeforeNewSelectionEnd.length - 1]
          .length + 1;

      editorMounted.executeEdits(
        null,
        [
          {
            range: {
              startLineNumber: selection.startLineNumber,
              startColumn: selection.startColumn,
              endLineNumber: selection.endLineNumber,
              endColumn: selection.endColumn,
            },
            text: replacedText,
          },
        ],
        [
          new monaco.Selection(
            selection.startLineNumber,
            selection.startColumn,
            newSelectionEndLineNumber,
            newSelectionEndColumn
          ),
        ]
      );
    };

    if (unicodeStylesEnabled) {
      editorMounted.addAction({
        id: "bold",
        label: "Bold",
        contextMenuGroupId: "fonts",
        run: () => toggleStyle.current!("bold"),
      });
      editorMounted.addAction({
        id: "italic",
        label: "Italic",
        contextMenuGroupId: "fonts",
        run: () => toggleStyle.current!("italic"),
      });
    }
  };

  const options: editor.IStandaloneEditorConstructionOptions = {
    copyWithSyntaxHighlighting: false,
    codeLens: false,
    minimap: {
      enabled: false,
    },
    wordWrap: "on",
    renderLineHighlight: "none",
    wrappingStrategy: "advanced",
    overviewRulerLanes: 0,
    glyphMargin: false,
    folding: false,
    lineNumbers: "off",
    lineDecorationsWidth: 5,
    lineNumbersMinChars: 0,
    scrollBeyondLastLine: false,
    selectionHighlight: false,
    occurrencesHighlight: "off",
    wordBasedSuggestions: "off",
    fontSize: 14,
    fontWeight: "400",
    fontFamily: "poppins",
    wrappingIndent: "none",
    guides: {
      indentation: false,
      highlightActiveIndentation: false,
    },
    unicodeHighlight: {
      ambiguousCharacters: false,
    },
    links: false,
  };

  function insertVariable(variableName: string) {
    if (!editorRef.current) {
      return;
    }

    const selection = editorRef.current.getSelection();
    if (selection == null) {
      return;
    }

    editorRef.current.executeEdits("variable-palette", [
      {
        range: selection,
        text: `{{${variableName}}}`,
        forceMoveMarkers: true,
      },
    ]);

    editorRef.current.focus();
  }

  function renderNewVariable() {
    if (newContactFieldFormOpen) {
      return (
        <AddContactFieldForm
          onSubmit={async name => {
            try {
              await onCreateContactField!(name);
              setNewContactFieldFormOpen(false);
            } catch (e) {
              console.error(e);
              snackbar.enqueueSnackbar(
                `Error creating contact field: ${
                  e instanceof Error ? e.message : e
                }`,
                {
                  variant: "error",
                }
              );
            }
          }}
          onCancel={() => setNewContactFieldFormOpen(false)}
          contactFieldNames={contactFields.map(field => field.name)}
        />
      );
    }

    if (onCreateContactField) {
      return (
        <Button
          sx={AddNewContactFieldButtonStyle}
          onClick={() => setNewContactFieldFormOpen(true)}
        >
          + New Contact Field
        </Button>
      );
    }

    return (
      <TooltipButton
        variant="contained"
        sx={AddNewContactFieldButtonStyle}
        tooltipText="Add contact fields from the Upload Contacts tab"
        disabled
      >
        + New Contact Field
      </TooltipButton>
    );
  }

  return (
    <EditorFormControl fullWidth style={{ paddingTop: theme.spacing(1) }}>
      <ScriptFieldLabel label={label} secondaryLabel={secondaryLabel} />
      <EditorWrapper
        sx={{
          ...(error
            ? { borderColor: palette.red }
            : focused
              ? {
                  borderColor: palette.darkBlue,
                  borderWidth: 2,
                  margin: "-1px -1px",
                }
              : {
                  borderColor: palette.midGray,
                }),
          // NOTE: this is a hack to get around a bug where it's not
          // possible to set the background color of a token. See:
          // https://github.com/microsoft/monaco-editor/issues/3868
          //
          // Instead, we create custom rules for variables and delimeters
          // and then override the styles for any spans other than spans
          // with the class "mtk1", which is the default class for standard
          // text. This is a hack and may break!
          "& .view-lines span span:not(.mtk1)": {
            color: palette.navy,
            backgroundColor: palette.midBlue,
          },
        }}
      >
        <Box
          sx={{
            // Left and right padding is 7px to compensate for the 5px internal padding
            // within the monaco editor i.e. 16px - 5px.
            padding: "10px 7px",
          }}
        >
          <Suspense fallback={<div>{value}</div>}>
            <MonacoEditor
              height={height}
              language="liquid"
              value={value}
              onMount={editorDidMount}
              options={options}
            />
          </Suspense>
          {media}
        </Box>
        <Box sx={{ height: "27px", backgroundColor: palette.lightGray }}>
          <FlexBox justifyContent="space-between">
            {unicodeStylesEnabled ? (
              <FlexBox alignItems="center" gap="10px">
                <ToggleButtonGroup
                  sx={{
                    [`& .${toggleButtonGroupClasses.grouped}`]: {
                      border: "none",
                      borderRight: "2px solid white",
                      borderRadius: 0,
                      width: "29px",
                      height: "27px",
                    },
                  }}
                >
                  <StyleButton
                    value="bold"
                    selected={boldSelected}
                    onChange={() => toggleStyle.current?.("bold")}
                    onMouseDown={event => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    disabled={!toggleStyle.current}
                  >
                    <FormatBold sx={{ fontSize: "14px" }} />
                  </StyleButton>
                  <StyleButton
                    value="italic"
                    selected={italicSelected}
                    onChange={() => toggleStyle.current?.("italic")}
                    onMouseDown={event => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    disabled={!toggleStyle.current}
                  >
                    <FormatItalic sx={{ fontSize: "14px" }} />
                  </StyleButton>
                </ToggleButtonGroup>
                {formattingError && (
                  <Typography variant="body3" color={palette.darkGray}>
                    {formattingError}
                  </Typography>
                )}
              </FlexBox>
            ) : (
              <Box />
            )}
            {additionalButtons && (
              <ToggleButtonGroup
                sx={{
                  [`& .${toggleButtonGroupClasses.grouped}`]: {
                    border: "none",
                    borderLeft: "2px solid white",
                    borderRadius: 0,
                    height: "27px",
                    fontSize: "10px",
                  },
                }}
              >
                {additionalButtons.map(buttonProps => (
                  <StyleButton {...buttonProps} />
                ))}
              </ToggleButtonGroup>
            )}
          </FlexBox>
        </Box>
      </EditorWrapper>
      <FormHelperText error={!!error}>{error}</FormHelperText>
      {!hideContactFields && (
        <Box sx={{ marginTop: 1 }}>
          <FlexBox alignItems="center" flexWrap="wrap">
            {contactFields.map(contactField => (
              <ContactFieldButton
                onClick={() => insertVariable(contactField.name)}
                onMouseDown={event => {
                  // otherwise we might blur the script editor, which can cause
                  // validation to run prematurely and prevent contact fields
                  // from being successfully added.
                  event.preventDefault();
                  event.stopPropagation();
                }}
                key={contactField.name}
              >
                {`{{${contactField.name}}}`}
              </ContactFieldButton>
            ))}
            {renderNewVariable()}
            <ExternalLink
              href="https://scaletowin.freshdesk.com/support/solutions/articles/66000524246-liquid-template"
              variant="body3"
              sx={{
                color: palette.darkGray,
                marginBottom: "5px",
                padding: "2px 6px 2px 6px",
              }}
            >
              How to use dynamic content &gt;
            </ExternalLink>
          </FlexBox>
        </Box>
      )}
    </EditorFormControl>
  );
}

export function FormikScriptField({
  name,
  shouldUpdate,
  fast,
  contactFields,
  onCreateContactField,
  label,
  secondaryLabel,
  onParse,
  hideContactFields = false,
  additionalButtons,
  media,
}: Pick<
  FormScriptFieldProps,
  | "contactFields"
  | "label"
  | "onParse"
  | "onCreateContactField"
  | "additionalButtons"
  | "media"
> & {
  name: string;
  shouldUpdate?: (props: any, prevProps: any) => boolean;
  fast?: boolean;
  secondaryLabel?: string | ReactElement;
  hideContactFields?: boolean;
}): ReactElement {
  const Component = fast ? FastField : Field;
  return (
    <Component name={name} shouldUpdate={shouldUpdate}>
      {({
        field,
        meta: meta,
        form,
      }: {
        field: FieldInputProps<unknown>;
        meta: FieldMetaProps<unknown>;
        form: FormikBag<unknown, unknown>;
      }) => {
        function handleChange(newVal: string) {
          form.setFieldValue(name, newVal, false).catch(console.error);
        }

        function handleParse(parseResult: LiquidTemplateParseResult) {
          if (parseResult.ok) {
            form.setFieldError(name, undefined);
          } else {
            form.setFieldError(name, parseResult.error);
          }

          onParse?.(parseResult);
        }

        return (
          <FormScriptField
            value={field.value as string}
            onChange={handleChange}
            onParse={handleParse}
            hideContactFields={hideContactFields}
            contactFields={contactFields}
            onCreateContactField={onCreateContactField}
            label={label}
            secondaryLabel={secondaryLabel}
            error={
              meta.error && meta.touched ? (meta.error as string) : undefined
            }
            onBlur={() => form.setFieldTouched(name, true, true)}
            additionalButtons={additionalButtons}
            media={media}
          />
        );
      }}
    </Component>
  );
}
